import type { NextPage } from "next";
import Car from "@src/interfaces/carDetails.interface";
import { getOffer } from "@app/store/offersApi";
import { wrapper } from "@app/store/store";
import Template from "@app/Template/Template";
import { ProductScreen } from "@features/product";
import { generateProductDescription, generateProductTitle } from "@features/seo";
import { ProfileSeller, publicProfileApi } from "@entities/profile";
import { configApi } from "@shared/lib/configApi";
import { OfferMeta } from "@shared/lib/interfaces/car.interface";
import { getVehicleCarSchema } from "@shared/lib/utils";
import { getBreadcrumbProductOfferSchema } from "@shared/lib/utils/getBreadcrumbProductOfferSchema";
import { CustomHead, DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";
import { SchemaJsonLd } from "@shared/ui/SchemaJsonLd";

const ProductPage: NextPage = ({
    data,
    meta,
    sellerData
}: {
    data?: Car;
    meta?: OfferMeta;
    sellerData?: ProfileSeller;
}) => {
    const loadError = !data || !data.id;
    const generatedTitle = generateProductTitle(data);
    const title = loadError ? "Ой! Такой страницы на нашем сайте нет :(" : generatedTitle;

    const generatedDescription = generateProductDescription(data);
    const description = loadError
        ? "Наверное, вы ошиблись при наборе адреса или перешли по неверной ссылке."
        : generatedDescription;
    const canonicalUrl = !loadError
        ? `${configApi.API_PUBLIC_DOMAIN}/product/${data?.id}`
        : undefined;
    const ogImageUrl = data?.pics?.[0]?.url || DEFAULT_OG_IMAGE;

    const vehicleSchema = data ? getVehicleCarSchema(data, canonicalUrl ?? "") : null;
    const breadcrumbSchema =
        data && meta ? getBreadcrumbProductOfferSchema(data, meta, canonicalUrl ?? "") : null;

    return (
        <Template bgColor="white">
            <CustomHead
                title={title}
                description={description}
                robots={"index,follow"}
                canonical={canonicalUrl}
                openGraph={{
                    title: title,
                    description: description,
                    url: canonicalUrl,
                    type: "product",
                    image: {
                        url: ogImageUrl,
                        alt: title
                    }
                }}
            />
            {vehicleSchema && breadcrumbSchema && (
                <SchemaJsonLd schemas={[vehicleSchema, breadcrumbSchema]} />
            )}
            <ProductScreen preloadedData={data} meta={meta} sellerData={sellerData} />
        </Template>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context) => {
    let { id } = context.params ?? {};

    if (!id || Array.isArray(id)) {
        id = "";
    }

    const { data: offerResponse } = await store.dispatch(getOffer.initiate(id));
    const offer = offerResponse?.offer ?? null;
    const meta = offerResponse?.meta ?? null;

    let sellerData = null;

    if (offer?.ownerUuid) {
        if (offer.isCompany) {
            const { data } = await store.dispatch(
                publicProfileApi.endpoints.getPublicCompanyProfileShortById.initiate(
                    offer.ownerUuid
                )
            );
            if (data) sellerData = data;
        }

        if (!offer.isCompany) {
            const { data } = await store.dispatch(
                publicProfileApi.endpoints.getPublicProfileById.initiate(offer.ownerUuid)
            );
            if (data) sellerData = data;
        }
    }

    context.res.statusCode = offer ? 200 : 404;
    return {
        props: {
            data: JSON.parse(JSON.stringify(offer || {})),
            meta: JSON.parse(JSON.stringify(meta)),
            ...(sellerData ? { sellerData } : {})
        }
    };
});

export default ProductPage;